import { FC, useMemo } from "react";
import { Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import ComparisonLinearProgress from "../../../components/charts/graph/ComparisonLinearProgress";
import NumericValueWidget from "../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../components/common/AmbientalKpiWidget";
import ContentLocked from "../../../components/common/ContentLocked";
import DropdownSelect from "../../../components/common/DropdownSelect";
import { MarketBaseType, MarketBaseWidget } from "../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../components/common/PermissionGridItem";
import { TransportPerspectiveSelector } from "../../../components/common/TransportPerspectiveSelector";
import { View } from "../../../components/layout/View";
import { PageType } from "../../../config/page.types";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../context/PageConfigrations/usePageConfigurationState";
import { EmissionsIntervalResultsDTO } from "../../../datasources/api-client/data-contracts";
import { useNavigationPreferences } from "../../../hooks/usePreferences";
import { getLockedContents } from "../../../permissions/permissionUtils";
import EmissionsIntervals from "../components/ResultsEmissionsIntervals";
import ResultsActivityEmissions from "./components/ResultsActivityEmissions";
import ResultsScopeEmissions from "./components/ResultsScopeEmissions";
import { useOverviewData } from "./useOverviewData";

type EmissionKey = keyof EmissionsIntervalResultsDTO;
// Keys for the dropdown select
type DropdownEmissionsKey = "co2Ttw" | "co2Wtw";

const ResultsOverview: FC = () => {
	const { t } = useTranslation(["feature.results", "common"]);

	const { appliedFilters } = useFiltersRegistry();
	const { navigationPreferences } = useNavigationPreferences();

	// Page configuration, stored in localStorage by PageType
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsOverview, {
		emissionsKey: "co2Ttw",
		intervalMode: "emissions",
		emissionsIntervalsPeriodType: "month",
		emissionScopeChartSelect: "tCO2e",
		activityScopeChartSelect: "tCO2e",
	});

	// Dropdown options for emission selection
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	// Update selected emission type in page config
	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as DropdownEmissionsKey });
	};

	/**
	 * Payload for data fetching
	 * Automatically re-memoized when filters change
	 */
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const { emissions, intervals, scopeEmissions, activityEmissions } = useOverviewData({
		payload,
		perspective: navigationPreferences.transportsPerspective,
		periodType: config.emissionsIntervalsPeriodType,
	});

	const { data: emissionsData, reload: reloadEmissions, isLoading: emissionsAreLoading } = emissions;
	const {
		data: emissionsIntervals,
		reload: reloadEmissionsIntervals,
		isLoading: loadingEmissionsIntervals,
	} = intervals;

	/**
	 * Resolve which key to use for the interval chart
	 * Energy → energyTtw
	 * Emissions → emission selected in overview
	 */
	const resolvedIntervalKey: EmissionKey =
		config.intervalMode === "energy" ? "energyTtw" : (config.emissionsKey as EmissionKey);
	const chartData = emissionsIntervals ? (emissionsIntervals[resolvedIntervalKey]?.data ?? []) : [];
	const chartMeasure = emissionsIntervals ? (emissionsIntervals[resolvedIntervalKey]?.measure ?? "") : "";

	const totalEmissions = emissionsData?.total?.current[config.emissionsKey as EmissionKey].value || 0;
	const fromEmissions = emissionsData?.greenRouter?.current[config.emissionsKey as EmissionKey].value || 0;
	const toEmissions = emissionsData?.thirdParty?.current[config.emissionsKey as EmissionKey].value || 0;

	const fromPercent = totalEmissions !== 0 ? (fromEmissions / totalEmissions) * 100 : 0;
	const toPercent = totalEmissions !== 0 ? (toEmissions / totalEmissions) * 100 : 0;

	const fromEmissionsDatasource = {
		name: "GreenRouter",
		value: fromPercent,
	};

	const toEmissionsDatasource = {
		name: t("common:third parties"),
		value: toPercent,
	};

	const isWtw = config.emissionsKey === "co2Wtw";

	// Get Marketbase values for the selected emission type
	const marketBase: MarketBaseType = {
		emissions: isWtw ? emissionsData?.total.current.co2MbWtw : emissionsData?.total.current.co2MbTtw,
		delta: isWtw
			? emissionsData?.total.current.marketBasedWtwDelta
			: emissionsData?.total.current.marketBasedTtwDelta,
		coverage: isWtw ? emissionsData?.mbTtwCoverage : emissionsData?.mbWtwCoverage,
		isLoading: emissionsAreLoading,
		onReload: reloadEmissions,
	};

	const titles = {
		totalEmissions: t("arcDetails/total emissions"),
		energy: t("emissions/widget/energyTtw"),
		// card title of the intervals chart (set by EmissionsIntervals)
		intervals: t("feature.units:overview/consumption trends and details"),
		scopeEmissions: t("emissions/scope/title"),
		activityEmissions: t("emissions/activity/title"),
		environmentalKpis: t("common:environmental kpis"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [emissions],
			names: [titles.totalEmissions, titles.energy, titles.environmentalKpis, titles.marketBased],
		},
		{ models: [intervals], names: [titles.intervals] },
		{ models: [scopeEmissions], names: [titles.scopeEmissions] },
		{ models: [activityEmissions], names: [titles.activityEmissions] },
	]);

	return (
		<View pageType={PageType.ResultsOverview}>
			<Grid container spacing={2}>
				<Grid size={12} display={"flex"} justifyContent={"flex-end"}>
					<Stack direction="row" spacing={2}>
						<DropdownSelect
							text={t("display for")}
							value={config.emissionsKey}
							loading={emissionsAreLoading}
							onChange={handleEmissionChange}
							dropdownItems={dropdownSelectEmissions}
							variant="standard"
						/>
						<TransportPerspectiveSelector />
					</Stack>
				</Grid>

				<Grid container size={12} spacing={2}>
					<PermissionGridItem
						models={[emissions]}
						minHeight={500}
						size={{ sm: 12, md: 6, lg: 4 }}
						spacing={2}
						container
					>
						<Grid size={12}>
							<NumericValueWidget
								title={titles.totalEmissions}
								delta={emissionsData?.total?.delta[config.emissionsKey as EmissionKey]}
								value={emissionsData?.total?.current[config.emissionsKey as EmissionKey].value}
								measure={emissionsData?.total?.current[config.emissionsKey as EmissionKey].measure}
								isLoading={emissionsAreLoading}
								onReload={reloadEmissions}
								graph={
									<ComparisonLinearProgress
										from={fromEmissionsDatasource}
										to={toEmissionsDatasource}
									/>
								}
							/>
						</Grid>

						<Grid size={12}>
							<NumericValueWidget
								title={titles.energy}
								delta={emissionsData?.total?.delta.energyTtw}
								value={emissionsData?.total?.current.energyTtw.value}
								measure={emissionsData?.total?.current.energyTtw.measure}
								isLoading={emissionsAreLoading}
								onReload={reloadEmissions}
							/>
						</Grid>
					</PermissionGridItem>

					{/* grows to fill the row when the emissions column is hidden */}
					<PermissionGridItem models={[intervals]} size={{ sm: 12, md: "grow" }} height={500}>
						<EmissionsIntervals
							selectTitle={t("emissions/intervals/trend")}
							onReload={reloadEmissionsIntervals}
							isLoading={loadingEmissionsIntervals}
							chartData={chartData}
							chartMeasure={chartMeasure}
							periodType={config.emissionsIntervalsPeriodType}
							setPeriodType={(v) =>
								setConfig({
									emissionsIntervalsPeriodType: v,
								})
							}
							intervalMode={config.intervalMode}
							onIntervalModeChange={(v) => setConfig({ intervalMode: v })}
						/>
					</PermissionGridItem>
				</Grid>

				<Grid container size={12} spacing={2}>
					<PermissionGridItem models={[scopeEmissions]} size="grow" minHeight={250}>
						<ResultsScopeEmissions
							title={titles.scopeEmissions}
							data={scopeEmissions.data}
							isLoading={scopeEmissions.isLoading}
							onReload={scopeEmissions.reload}
							chartMode={config.emissionScopeChartSelect}
							onChartModeChange={(v) => setConfig({ emissionScopeChartSelect: v })}
						/>
					</PermissionGridItem>

					<PermissionGridItem models={[activityEmissions]} size="grow" minHeight={250}>
						<ResultsActivityEmissions
							title={titles.activityEmissions}
							data={activityEmissions.data}
							isLoading={activityEmissions.isLoading}
							onReload={activityEmissions.reload}
							keyEmissions={config.emissionsKey}
							chartMode={config.activityScopeChartSelect}
							onChartModeChange={(v) => setConfig({ activityScopeChartSelect: v })}
						/>
					</PermissionGridItem>
				</Grid>

				<PermissionGridItem models={[emissions]} size={12}>
					<AmbientalKpiWidget
						bio={emissionsData?.total?.current.co2Bio}
						pmx={emissionsData?.total?.current.pmx}
						bioCoverage={isWtw ? emissionsData?.biogenicWtwCoverage : emissionsData?.biogenicTtwCoverage}
						pmxCoverage={isWtw ? emissionsData?.pmxWtwCoverage : emissionsData?.pmxTtwCoverage}
						loading={emissionsAreLoading}
						onReload={reloadEmissions}
					/>
				</PermissionGridItem>

				<PermissionGridItem models={[emissions]} size={12}>
					<MarketBaseWidget marketBase={marketBase} />
				</PermissionGridItem>

				{lockedContents.length > 0 && (
					<Grid size={12}>
						<ContentLocked items={lockedContents} />
					</Grid>
				)}
			</Grid>
		</View>
	);
};

export default ResultsOverview;
